/**
 * Shared photo-URL helpers used across the app.
 *
 * Central place to:
 *  1. Detect and skip invalid / placeholder photo URLs (e.g. pure-numeric IDs
 *     from the old system that resolve to https://match2.findu.love/up/1).
 *  2. Resolve a raw photo value into a displayable URL, handling legacy
 *     (match2.findu.love), new-system (/uploads/…), and data-URL formats.
 *  3. Filter gallery arrays to remove junk entries.
 */

/**
 * Returns true if `url` is an invalid / placeholder photo that should be
 * hidden rather than displayed.  Covers:
 *  - null / undefined / empty
 *  - pure-numeric strings ("1", "42", "00")  → old-system row-IDs, not photos
 *  - resolved URLs like https://match2.findu.love/up/1  (trailing 1-5 digits)
 *  - the string "0"
 */
export function isInvalidPhotoUrl(url) {
  if (!url) return true;
  const s = String(url).trim();
  if (!s) return true;
  // Pure digits (any length) — old DB row IDs, not real filenames
  if (/^\d+$/.test(s)) return true;
  // Legacy base URL followed by only 1-5 digits
  if (/^https?:\/\/match2\.findu\.love\/up\/\d{1,5}$/i.test(s)) return true;
  return false;
}

/**
 * Heuristic: does `s` look like a raw (unprefixed) base64-encoded image?
 * Stored legacy entries sometimes have the `data:image/...;base64,` prefix
 * stripped, leaving a long base64 blob. Passing that as an <img> src causes
 * the browser to treat it as a relative URL and emit 414 errors.
 */
export function isRawBase64Image(s) {
  if (!s || typeof s !== 'string') return false;
  if (s.length < 200) return false;
  if (/^(https?:|data:|\/)/i.test(s)) return false;
  // JPEG base64 typically starts with /9j/; PNG with iVBOR; GIF with R0lGOD
  if (/^(\/9j\/|iVBOR|R0lGOD)/.test(s)) return true;
  // Otherwise: long, only base64 chars → very likely bare base64
  return /^[A-Za-z0-9+/=]+$/.test(s);
}

/**
 * Convert a raw photo value into a displayable URL.
 * Returns null for invalid / placeholder values.
 */
export function resolvePhotoUrl(url) {
  if (isInvalidPhotoUrl(url)) return null;
  const s = String(url).trim();
  // Full URL or data-URL → use as-is (swap _s. → _b. for hi-res variant)
  if (s.startsWith('http://') || s.startsWith('https://') || s.startsWith('data:')) {
    return s.includes('_s.') ? s.replace('_s.', '_b.') : s;
  }
  // New-system uploads
  if (s.startsWith('/uploads/') || s.startsWith('/up/')) {
    return s;
  }
  // Raw base64 blob with the data-URL prefix stripped: re-attach so the
  // browser renders it inline instead of issuing a request (→ 414).
  if (isRawBase64Image(s)) {
    const kind = s.startsWith('iVBOR') ? 'png' : s.startsWith('R0lGOD') ? 'gif' : 'jpeg';
    return `data:image/${kind};base64,${s}`;
  }
  // Legacy photos from old system
  const bigUrl = s.includes('_s.') ? s.replace('_s.', '_b.') : s;
  return 'https://match2.findu.love/up/' + bigUrl;
}

/**
 * Parse + sanitise a gallery field (could be JSON string, array, or null)
 * and return an array of valid, displayable photo URLs.
 */
export function parseGallery(gallery) {
  let arr = [];
  if (!gallery) return arr;
  if (typeof gallery === 'string') {
    try { arr = JSON.parse(gallery); } catch { arr = [gallery]; }
  } else if (Array.isArray(gallery)) {
    arr = gallery;
  }
  return arr
    .map(resolvePhotoUrl)
    .filter(Boolean); // drop nulls (invalid entries)
}
